import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Shield, CheckCircle } from 'lucide-react';
import PageHero from '../components/PageHero';
import CTASection from '../components/CTASection';
import SEO from '../components/SEO';

const DangerousGoods = () => {
  const capabilities = [
    'Classification support',
    'Packaging coordination',
    'Labelling and documentation',
    'IATA, ADR and IMDG transport coordination',
    'Safety Data Sheet (SDS) review',
    'Carrier acceptance checks',
    'Compliant routing',
    'Lithium battery movement coordination',
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Dangerous Goods Logistics | Aero Time Critical" description="Dangerous goods coordination: classification support, packaging, labelling, documentation, SDS review and IATA/ADR/IMDG compliant routing via trained staff and approved partners." path="/dangerous-goods" />
      <PageHero
        badge="SPECIALIST HANDLING"
        BadgeIcon={AlertTriangle}
        title="Dangerous Goods &"
        titleAccent="Specialist Logistics"
        description="We coordinate the compliant transport of hazardous materials through appropriately trained staff and approved carrier partners, covering classification, documentation and safe routing."
        image="https://customer-assets-m6fa6gv7.emergentagent.net/job_cargo-urgent/artifacts/xp86nemq_Dangerous%20goods.jpg"
      />

      <section className="bg-[#F7F9FB] py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#0089D0] font-semibold tracking-wide uppercase text-sm">How We Support You</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-3 mb-8 text-[#003057]">
                Compliant DG Coordination
              </h2>
              <div className="grid grid-cols-1 gap-4">
                {capabilities.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-3 p-4 bg-white rounded-lg border-l-4 border-[#0089D0] shadow-sm"
                  >
                    <CheckCircle className="text-[#0089D0] flex-shrink-0" size={22} />
                    <span className="text-[#003057] font-medium">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <img
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_cargo-urgent/artifacts/xp86nemq_Dangerous%20goods.jpg"
                alt="Dangerous goods hazard classification labels for compliant transport"
                className="w-full h-[300px] object-cover rounded-lg shadow-xl"
                loading="lazy"
              />
              <div className="bg-[#003057] text-white p-8 rounded-lg">
                <Shield size={56} className="mb-5 text-[#C9A227]" />
                <h3 className="text-2xl font-bold mb-4 text-white">Safety & Compliance</h3>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  We work with appropriately trained staff and approved carrier partners to ensure hazardous
                  shipments are prepared, documented and routed in line with international regulations.
                </p>
                <ul className="space-y-3">
                  {['IATA DGR transport coordination', 'ADR road transport coordination', 'IMDG sea freight coordination'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle size={22} className="text-[#C9A227]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <CTASection
        title="Need Dangerous Goods Support?"
        description="Contact our team for expert guidance on compliant hazardous shipments"
        buttonText="Request DG Quote"
      />
    </div>
  );
};

export default DangerousGoods;
